import { cn } from "@/lib/utils";
import {
  formatCentsBRL,
  formatCentsCompact,
  formatDurationMs,
  formatMoneyRange,
  formatPretensao,
  formatUsd,
  toCents,
  type MoneyUnit,
} from "@/lib/format";
import { EmptyValue } from "./EmptyValue";

interface MoneyProps {
  value: number | null | undefined;
  /** Unidade de armazenamento. Verba e finance: centavos; valor_projeto: reais. */
  unit: MoneyUnit;
  compact?: boolean;
  className?: string;
}

export function Money({ value, unit, compact = false, className }: MoneyProps) {
  if (value == null) return <EmptyValue />;
  const cents = toCents(value, unit);
  return (
    <span className={cn("tabular-nums", className)} title={compact ? formatCentsBRL(cents) : undefined}>
      {compact ? formatCentsCompact(cents) : formatCentsBRL(cents)}
    </span>
  );
}

interface MoneyRangeProps {
  min: number | null | undefined;
  max: number | null | undefined;
  unit: MoneyUnit;
  compact?: boolean;
  /** Ex.: "/mês". */
  suffix?: string;
  className?: string;
}

export function MoneyRange({ min, max, unit, compact = true, suffix, className }: MoneyRangeProps) {
  const text = formatMoneyRange(min, max, { unit, compact });
  if (!text) return <EmptyValue />;
  return (
    <span className={cn("tabular-nums", className)}>
      {text}
      {suffix && <span className="ml-0.5 font-normal text-muted-foreground">{suffix}</span>}
    </span>
  );
}

/** Pretensão salarial (texto livre no banco). */
export function Pretensao({ value, className }: { value: string | number | null | undefined; className?: string }) {
  const text = formatPretensao(value);
  if (!text) return <EmptyValue label="Não informada" />;
  return <span className={cn("tabular-nums", className)}>{text}</span>;
}

/** Custo em dólar ESTIMADO (tabela de preços x uso), nunca valor faturado. */
export function UsdEstimate({ value, className }: { value: number | null | undefined; className?: string }) {
  if (value == null) return <EmptyValue />;
  return (
    <span className={cn("tabular-nums", className)} title="Estimativa pela tabela de preços, não é o valor faturado">
      {formatUsd(value)}
    </span>
  );
}

/** Duração de uma execução: "850 ms", "3 min 5 s". */
export function Duration({ milliseconds, className }: { milliseconds: number | null | undefined; className?: string }) {
  if (milliseconds == null) return <EmptyValue />;
  return <span className={cn("tabular-nums", className)}>{formatDurationMs(milliseconds)}</span>;
}
